/**
 * Event Builder constants — section ids, venue ids, defaults and copy strings.
 * Source: docs/event-builder-redesign-plan.md (3.3, 6.*, 9.2, Appendix A/B).
 *
 * Nothing here changes a payload contract: the values are the ones the old wizard pages
 * already send (venue ids from the `venues` table, E/D enums, payment/refund enums).
 */

import {
  GOOGLE_MEET_APP_ID,
  ZOOM_APP_ID,
  MICROSOFT_TEAMS_ID,
  STRIPE_APP_ID,
  PAYPAL_APP_ID,
  GOOGLE_CALENDAR_ID,
  OUTLOOK_CALENDAR_ID,
} from "../../../../data/integration-app";

export const E = "E";
export const D = "D";

// ---- Sections (D7: `?section=` in the URL) ------------------------------------------------
export const SECTIONS = {
  QUICK: "quick",
  AVAILABILITY: "availability",
  HOSTS: "hosts",
  INVITEE: "invitee",
  NOTIFICATIONS: "notifications",
  PAYMENTS: "payments",
};

export const SECTION_ORDER = [
  SECTIONS.QUICK,
  SECTIONS.AVAILABILITY,
  SECTIONS.HOSTS,
  SECTIONS.INVITEE,
  SECTIONS.NOTIFICATIONS,
  SECTIONS.PAYMENTS,
];

export const isValidSection = (value) => SECTION_ORDER.includes(value);

// Which save endpoint ("step") each section's fields belong to (3.3 state groups)
export const STEPS = {
  TYPE: "type",
  DETAILS: "details",
  AVAILABILITY: "availability",
  ADVANCED: "advanced",
};
export const STEP_ORDER = [STEPS.TYPE, STEPS.DETAILS, STEPS.AVAILABILITY, STEPS.ADVANCED];

// Which nav row shows the red dot for a step's field errors (6.B)
export const STEP_TO_NAV_SECTION = {
  [STEPS.TYPE]: SECTIONS.QUICK,
  [STEPS.DETAILS]: SECTIONS.INVITEE,
  [STEPS.AVAILABILITY]: SECTIONS.AVAILABILITY,
  [STEPS.ADVANCED]: SECTIONS.INVITEE,
};

// ---- Kind -----------------------------------------------------------------------------------
export const KIND = { FREE: "free", PAID: "paid" };
export const kindFromQuery = (type) => (type === "paid-event" ? KIND.PAID : KIND.FREE);
export const queryFromKind = (kind) => (kind === KIND.PAID ? "paid-event" : "free-event");

// ---- Venues (`venues` table ids; 6 = GoToMeeting retired, excluded) --------------------------
export const VENUE = {
  IN_PERSON: 1,
  PHONE: 2,
  GOOGLE_MEET: 3,
  ZOOM: 4,
  MICROSOFT_TEAMS: 5,
  OTHER: 7,
};

// Conferencing venue → integration app id in `admin/connected-venues` (6.C block 4)
export const VENUE_APP_ID = {
  [VENUE.GOOGLE_MEET]: GOOGLE_MEET_APP_ID,
  [VENUE.ZOOM]: ZOOM_APP_ID,
  [VENUE.MICROSOFT_TEAMS]: MICROSOFT_TEAMS_ID,
};
export const CONFERENCING_VENUES = [VENUE.GOOGLE_MEET, VENUE.ZOOM, VENUE.MICROSOFT_TEAMS];

export const PAYMENT_APP_IDS = { STRIPE: STRIPE_APP_ID, PAYPAL: PAYPAL_APP_ID };
export const CALENDAR_APP_IDS = { GOOGLE: GOOGLE_CALENDAR_ID, OUTLOOK: OUTLOOK_CALENDAR_ID };

// Card copy (6.C block 4; Zoom/Teams/Other added per D13)
export const VENUE_CARDS = [
  { id: VENUE.GOOGLE_MEET, title: "Google Meet", sub: "Link added automatically", icon: "video", iconClass: "meet" },
  { id: VENUE.ZOOM, title: "Zoom", sub: "Link added automatically", icon: "video", iconClass: "meet" },
  { id: VENUE.MICROSOFT_TEAMS, title: "Microsoft Teams", sub: "Link added automatically", icon: "video", iconClass: "meet" },
  { id: VENUE.PHONE, title: "Phone call", sub: "Collect invitee number", icon: "phone" },
  { id: VENUE.IN_PERSON, title: "In person", sub: "Set an address", icon: "pin" },
  { id: VENUE.OTHER, title: "Other", sub: "Add your own instructions", icon: "link" },
];

export const VENUE_NAMES = VENUE_CARDS.reduce((acc, v) => {
  acc[v.id] = v.title;
  return acc;
}, {});

// Venue detail enums (Appendix A.1)
export const INVITEE_CALL_STATUS = { ME: "me", INVITEE: "invitee" };
export const VENUE_LOCATION_STATUS = {
  DISPLAY_BOOKING: "display_location_booking",
  DISPLAY_AFTER_CONFIRMED: "display_booking_after_confirmed",
  INVITEE_ADD_LOCATION: "invitee_add_location", // legacy; shown only when already stored (E12)
};
export const VENUE_OTHER_DISPLAY_STATUS = { BOOKING_NOTES: "1", AFTER_CONFIRMED: "2" };

// ---- Format / duration -------------------------------------------------------------------------
export const FORMAT = { ONE_TO_ONE: "1", GROUP: "2" };
export const GROUP_DEFAULT_CAPACITY = 10;
export const GROUP_MIN_CAPACITY = 2;

export const DURATION_PRESETS = [15, 30, 45, 60];
export const DURATION_MIN = 5;
export const DURATION_STEP = 5;
export const DEFAULT_DURATION = 30;

// ---- Colour (6.C block 6) -----------------------------------------------------------------------
export const COLOR_SWATCHES = ["#286875", "#3578c6", "#7256b8", "#d06c3c", "#c04769", "#36825d"];
export const DEFAULT_COLOR = "#286875";

// ---- Schedules ------------------------------------------------------------------------------------
export const WEEKDAYS = [
  { id: 1, name: "Monday", short: "Mon" },
  { id: 2, name: "Tuesday", short: "Tue" },
  { id: 3, name: "Wednesday", short: "Wed" },
  { id: 4, name: "Thursday", short: "Thu" },
  { id: 5, name: "Friday", short: "Fri" },
  { id: 6, name: "Saturday", short: "Sat" },
  { id: 7, name: "Sunday", short: "Sun" },
];
export const DEFAULT_WORK_START = "09:00";
export const DEFAULT_WORK_END = "17:00";
export const DEFAULT_WORK_DAYS = [1, 2, 3, 4, 5];

export const SCHEDULE_SOURCE = { TEMPLATE: "template", CUSTOM: "custom" };
export const SCHEDULE_TYPE = { EVENT: "event", EVENT_STAFF: "event_staff" };
export const SCHEDULE_ROW_TYPE = { WEEKLY: "weekly", CUSTOM: "custom" };

// ---- Validity / rules (6.D) ------------------------------------------------------------------------
export const VALIDITY = { INDEFINITE: "indefinitly", VALID_OVER: "valid_over", DATE_RANGE: "date_range" };
export const VALID_OVER_DEFAULT_DAYS = 60;
export const DATE_RANGE_DEFAULT_SPAN_DAYS = 14;

export const MIN_NOTICE_OPTIONS = [
  { value: 0, label: "No minimum" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 240, label: "4 hours" },
  { value: 1440, label: "24 hours" },
  { value: 2880, label: "48 hours" },
];
export const MAX_NOTICE_OPTIONS = [
  { value: 0, label: "No maximum" },
  { value: 30 * 1440, label: "30 days" },
  { value: 60 * 1440, label: "60 days" },
  { value: 90 * 1440, label: "90 days" },
];
export const INCREMENT_OPTIONS = [
  { value: 0, label: "Default (15 minutes)" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "60 minutes" },
];
export const BUFFER_OPTIONS = [
  { value: 0, label: "No buffer" },
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
];

// ---- Staff assignment (D16 / 6.E) ---------------------------------------------------------------------
export const STAFF_ASSIGNMENT_TYPE = { MY_EVENT: "1", STAFF: "2" };
export const STAFF_CHOOSE = { ALLOW_INVITEE: "allow_invitee", RANDOM: "random_choose" };
export const STAFF_METHOD = { ROUND_ROBIN: "round-robin", EVENT_ROTATION: "event-rotation" };
export const ASSIGNMENT_MODE = { MY: "my", ASSIGN: "assign", INVITEE: "invitee", AUTO: "auto" };

// ---- Questions (6.F) ------------------------------------------------------------------------------------
export const QUESTION_TYPES = [
  { value: "text", label: "Short answer" },
  { value: "multiline", label: "Long answer" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Multiple choice" },
  { value: "checkbox", label: "Checkboxes" },
];
export const QUESTION_TYPES_WITH_OPTIONS = ["select", "radio", "checkbox"];
export const DEFAULT_QUESTION = {
  question: "Please share anything that will help prepare for our meeting.",
  question_type: "multiline",
  show_status: E,
  mandatory_status: D,
  details: [],
};
export const questionTypeLabel = (value) =>
  (QUESTION_TYPES.find((t) => t.value === value) || {}).label || value;

export const CONFIRMATION_PAGE = { DEFAULT: "default", EXTERNAL: "external" };

// ---- Payments (6.C block 7 / 6.H) ---------------------------------------------------------------------
export const PAYMENT_TYPE = { FULL: "full_payment", PARTIAL: "partial_payment", DEPOSIT: "deposit" };
export const FLAT_PERCENT = { PERCENT: "percent", FLAT: "flat" };
export const PAYMENT_TIMING = { FULL: "full", DEPOSIT: "deposit", AT_EVENT: "at_event" };
export const REFUND_TYPE = { NONE: "none", PARTIAL: "partial", FULL: "full" };
export const REFUND_CUTOFF_DEFAULT_MINUTES = 1440;
export const REFUND_CUTOFF_OPTIONS = [
  { value: 0, label: "No cut-off" },
  { value: 1440, label: "24 hours before start" },
  { value: 2880, label: "48 hours before start" },
  { value: 10080, label: "7 days before start" },
];
// Quick setup refund select (6.C block 7): value → { refund_type, refund_cutoff_time }
export const QUICK_REFUND_OPTIONS = [
  { value: "full_24h", label: "Full refund until 24 hours before", refund_type: REFUND_TYPE.FULL, refund_cutoff_time: 1440 },
  { value: "none", label: "No refunds", refund_type: REFUND_TYPE.NONE, refund_cutoff_time: 0 },
  { value: "always", label: "Always refundable", refund_type: REFUND_TYPE.FULL, refund_cutoff_time: 0 },
];
export const DEFAULT_CURRENCY = "USD";

// ---- Image upload (6.F / E14) — config('constants.image_extensions') on the server ----------------
export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "tiff"];
export const IMAGE_ACCEPT = IMAGE_EXTENSIONS.map((e) => `.${e}`).join(",");

// ---- Routes -------------------------------------------------------------------------------------------
export const ROUTES = {
  BUILDER: "/admin/events/builder",
  PICKER: "/admin/events/create/list",
  LIST: "/admin/events/list",
  SUBSCRIPTION: "/admin/manage-subscription",
  CALENDAR_INTEGRATION: "/admin/calendar-integration",
  PAYMENT_INTEGRATIONS: "/integrations?tab=payments",
  ZOOM_INTEGRATION: "/integrations/zoom",
  MEET_INTEGRATION: "/integrations/meet",
  TEAMS_INTEGRATION: "/integrations/ms-teams",
  EVENT_SETTINGS: "/admin/settings/events",
  COUPONS: "/admin/settings/discounts", // "Marketing › Coupons" lives under Settings › Discounts in this app
};

// ---- Copy (Appendix B; verbatim) ------------------------------------------------------------------------
export const COPY = {
  topbar: {
    newFree: "New free event",
    newPaid: "New paid event",
    editing: (name) => `Editing \u00B7 ${name || "event"}`,
    saved: "All changes saved",
    saving: "Saving\u2026",
    error: "Couldn't save \u00B7 Retry",
    unsaved: "Draft \u00B7 not saved yet",
    unsavedChanges: "Unsaved changes", // dirty step blocked by a required item (existing event)
    preview: "Preview",
    create: "Create event",
    save: "Save event",
  },
  sidebar: {
    back: "Event types",
    newEvent: "New event",
    additional: "ADDITIONAL SETTINGS",
    tipTitle: "Ready by default",
    tipBody: "MeetOcto applies your account preferences automatically.",
  },
  sections: {
    [SECTIONS.QUICK]: "Event Basics",
    [SECTIONS.AVAILABILITY]: "Availability & rules",
    [SECTIONS.HOSTS]: "Hosts & assignment",
    [SECTIONS.INVITEE]: "Invitee experience",
    [SECTIONS.NOTIFICATIONS]: "Notifications & reminders",
    [SECTIONS.PAYMENTS]: "Payments",
  },
  footer: {
    complete: "Everything required is complete",
    remaining: (n) => `${n} required item${n === 1 ? "" : "s"} left`,
    createFree: "Create & copy link",
    createPaid: "Create paid event & copy link",
    savePublished: "Save & copy link",
  },
  toast: {
    linkCopied: "Booking link copied",
    copied: "Link copied",
  },
  note: "Changes are saved automatically. Every original event-builder option is included in these grouped pages.",
};
